import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlined';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import KeyOutlinedIcon from '@mui/icons-material/KeyOutlined';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import PersonOutlineIcon from '@mui/icons-material/PersonOutlined';
import SearchIcon from '@mui/icons-material/Search';
import { api, type AdminUser, type AppSettings, type Role } from '../../lib/api';
import { useAuthStore } from '../../store/authStore';
import { useDepartmentStore } from '../../store/departmentStore';
import { ConfirmDialog } from '../ConfirmDialog';
import { DepartmentsTab } from './DepartmentsTab';
import { TemplatesTab } from './TemplatesTab';

interface Props {
  open: boolean;
  onClose: () => void;
}

function formatDate(iso: string | null): string {
  if (!iso) return 'Never';
  return new Date(iso).toLocaleString([], { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

/** Shows a one-time temporary password with a copy button. */
function TempPasswordNotice({ email, password }: { email: string; password: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Alert severity="success" sx={{ '& .MuiAlert-message': { width: '100%' } }}>
      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
        Temporary password for {email}
      </Typography>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <Box
          component="code"
          sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 14, px: 1, py: 0.5, bgcolor: 'background.paper', borderRadius: 1 }}
        >
          {password}
        </Box>
        <Button
          size="small"
          color="neutral"
          startIcon={<ContentCopyOutlinedIcon />}
          onClick={() => {
            void navigator.clipboard.writeText(password).then(() => setCopied(true));
          }}
        >
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </Stack>
      <Typography variant="caption" component="p" sx={{ mt: 0.75, color: 'inherit' }}>
        Share it with them privately. It's shown only once, and they'll set their own password at first sign-in.
      </Typography>
    </Alert>
  );
}

function AddUserDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<Role>('member');
  const [departmentId, setDepartmentId] = useState('');
  const departments = useDepartmentStore((s) => s.departments);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<{ email: string; password: string } | null>(null);

  const reset = () => {
    setEmail('');
    setName('');
    setRole('member');
    setDepartmentId('');
    setError(null);
    setCreated(null);
  };

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await api.admin.createUser({ email, name, role, departmentId: departmentId || null });
      setCreated({ email: r.user.email, password: r.tempPassword });
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not add user');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      aria-labelledby="add-user-title"
      slotProps={{ transition: { onExited: reset } }}
    >
      <DialogTitle id="add-user-title" component="h2" variant="h6">
        Add user
      </DialogTitle>
      <DialogContent>
        {created ? (
          <TempPasswordNotice email={created.email} password={created.password} />
        ) : (
          <Stack spacing={2}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField label="Name" fullWidth autoFocus value={name} onChange={(e) => setName(e.target.value)} />
            <TextField
              label="Email"
              type="email"
              fullWidth
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <TextField select label="Role" fullWidth value={role} onChange={(e) => setRole(e.target.value as Role)}>
              <MenuItem value="member">
                <ListItemText primary="Member" secondary="Creates and edits their own decks" />
              </MenuItem>
              <MenuItem value="admin">
                <ListItemText primary="Admin" secondary="Also manages users and settings" />
              </MenuItem>
            </TextField>
            <TextField
              select
              label="Department (optional)"
              fullWidth
              value={departmentId}
              onChange={(e) => setDepartmentId(e.target.value)}
              helperText="Used for department announcements and read reports"
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {departments.map((d) => (
                <MenuItem key={d.id} value={d.id}>
                  {d.name}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {created ? (
          <Button variant="contained" onClick={onClose}>
            Done
          </Button>
        ) : (
          <>
            <Button variant="outlined" color="neutral" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={busy || !email}
              onClick={() => void submit()}
              startIcon={busy ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              Add user
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

function UsersTab() {
  const me = useAuthStore((s) => s.user);
  const departments = useDepartmentStore((s) => s.departments);
  const refreshDepartments = useDepartmentStore((s) => s.refresh);
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [usage, setUsage] = useState<Record<string, number>>({});
  const [query, setQuery] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [menu, setMenu] = useState<{ anchor: HTMLElement; user: AdminUser } | null>(null);
  const [confirm, setConfirm] = useState<{ kind: 'delete' | 'reset' | 'deactivate'; user: AdminUser } | null>(null);
  const [tempPassword, setTempPassword] = useState<{ email: string; password: string } | null>(null);

  const load = useCallback(
    () =>
      Promise.all([api.admin.listUsers(), api.admin.usage()])
        .then(([u, usageRes]) => {
          setUsers(u.users);
          setUsage(usageRes.usage);
        })
        .catch((err) => {
          setError(err instanceof Error ? err.message : 'Could not load users');
          setUsers([]);
        }),
    [],
  );

  useEffect(() => {
    void load();
    void refreshDepartments();
  }, [load, refreshDepartments]);

  const act = async (fn: () => Promise<unknown>) => {
    setError(null);
    try {
      await fn();
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    }
  };

  const filtered = (users ?? []).filter((u) =>
    `${u.name} ${u.email}`.toLowerCase().includes(query.trim().toLowerCase()),
  );

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1.5}>
        <TextField
          placeholder="Search users"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          sx={{ flex: 1 }}
          slotProps={{
            htmlInput: { 'aria-label': 'Search users' },
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18 }} />
                </InputAdornment>
              ),
            },
          }}
        />
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAdding(true)}>
          Add user
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {tempPassword && <TempPasswordNotice email={tempPassword.email} password={tempPassword.password} />}

      {users === null ? (
        <Box sx={{ display: 'grid', placeItems: 'center', py: 6 }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 2.2fr) minmax(0, 0.9fr) minmax(0, 1.3fr) minmax(0, 1.2fr) 56px 40px',
              gap: 1.5,
              px: 2,
              py: 1,
              bgcolor: 'chrome.subtle',
              typography: 'caption',
              fontWeight: 600,
            }}
          >
            <span>User</span>
            <span>Role</span>
            <span>Department</span>
            <span>Last sign-in</span>
            <Tooltip title="AI requests today">
              <span>AI today</span>
            </Tooltip>
            <span />
          </Box>
          {filtered.map((u) => (
            <Box
              key={u.id}
              sx={{
                display: 'grid',
                gridTemplateColumns: 'minmax(0, 2.2fr) minmax(0, 0.9fr) minmax(0, 1.3fr) minmax(0, 1.2fr) 56px 40px',
                gap: 1.5,
                alignItems: 'center',
                px: 2,
                py: 1.25,
                borderTop: 1,
                borderColor: 'divider',
                opacity: u.active ? 1 : 0.6,
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
                  <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                    {u.name || u.email.split('@')[0]}
                  </Typography>
                  {u.id === me?.id && <Chip size="small" label="You" sx={{ height: 20 }} />}
                  {!u.active && <Chip size="small" label="Deactivated" sx={{ height: 20 }} />}
                  {u.active && u.mustChangePassword && (
                    <Tooltip title="Hasn't set their own password yet">
                      <Chip size="small" variant="outlined" label="Invited" sx={{ height: 20 }} />
                    </Tooltip>
                  )}
                </Stack>
                <Typography variant="caption" noWrap component="div">
                  {u.email}
                </Typography>
              </Box>
              <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', color: u.role === 'admin' ? 'primary.main' : 'text.secondary' }}>
                {u.role === 'admin' ? (
                  <AdminPanelSettingsOutlinedIcon sx={{ fontSize: 16 }} />
                ) : (
                  <PersonOutlineIcon sx={{ fontSize: 16 }} />
                )}
                <Typography variant="body2" sx={{ color: 'inherit' }}>
                  {u.role === 'admin' ? 'Admin' : 'Member'}
                </Typography>
              </Stack>
              <TextField
                select
                size="small"
                value={departments.some((d) => d.id === u.departmentId) ? (u.departmentId ?? '') : ''}
                onChange={(e) => void act(() => api.admin.updateUser(u.id, { departmentId: e.target.value || null }))}
                slotProps={{ htmlInput: { 'aria-label': `Department for ${u.email}` }, select: { displayEmpty: true } }}
                sx={{ minWidth: 0, '& .MuiSelect-select': { py: 0.5, fontSize: 13 } }}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {departments.map((d) => (
                  <MenuItem key={d.id} value={d.id}>
                    {d.name}
                  </MenuItem>
                ))}
              </TextField>
              <Typography variant="body2" color="text.secondary" noWrap>
                {formatDate(u.lastLoginAt)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {usage[u.id] ?? 0}
              </Typography>
              <IconButton
                size="small"
                aria-label={`Actions for ${u.email}`}
                onClick={(e) => setMenu({ anchor: e.currentTarget, user: u })}
              >
                <MoreHorizIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
          {!filtered.length && (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4, borderTop: 1, borderColor: 'divider' }}>
              No users match “{query}”.
            </Typography>
          )}
        </Box>
      )}

      <Menu
        anchorEl={menu?.anchor}
        open={Boolean(menu)}
        onClose={() => setMenu(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { width: 220 } } }}
      >
        {menu && menu.user.id !== me?.id && (
          <MenuItem
            onClick={() => {
              const u = menu.user;
              setMenu(null);
              void act(() => api.admin.updateUser(u.id, { role: u.role === 'admin' ? 'member' : 'admin' }));
            }}
          >
            <ListItemIcon>
              {menu.user.role === 'admin' ? <PersonOutlineIcon fontSize="small" /> : <AdminPanelSettingsOutlinedIcon fontSize="small" />}
            </ListItemIcon>
            <ListItemText primary={menu.user.role === 'admin' ? 'Make member' : 'Make admin'} />
          </MenuItem>
        )}
        {menu && (
          <MenuItem
            onClick={() => {
              setConfirm({ kind: 'reset', user: menu.user });
              setMenu(null);
            }}
          >
            <ListItemIcon>
              <KeyOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Reset password" />
          </MenuItem>
        )}
        {menu && menu.user.id !== me?.id && (
          <MenuItem
            onClick={() => {
              const u = menu.user;
              setMenu(null);
              if (u.active) setConfirm({ kind: 'deactivate', user: u });
              else void act(() => api.admin.updateUser(u.id, { active: true }));
            }}
          >
            <ListItemIcon>
              {menu.user.active ? <BlockIcon fontSize="small" /> : <CheckCircleOutlineIcon fontSize="small" />}
            </ListItemIcon>
            <ListItemText primary={menu.user.active ? 'Deactivate' : 'Reactivate'} />
          </MenuItem>
        )}
        {menu && menu.user.id !== me?.id && (
          <MenuItem
            sx={{ color: 'error.main' }}
            onClick={() => {
              setConfirm({ kind: 'delete', user: menu.user });
              setMenu(null);
            }}
          >
            <ListItemIcon sx={{ color: 'inherit' }}>
              <DeleteOutlineIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Delete user" />
          </MenuItem>
        )}
      </Menu>

      <AddUserDialog open={adding} onClose={() => setAdding(false)} onCreated={() => void load()} />

      <ConfirmDialog
        open={confirm?.kind === 'reset'}
        title="Reset password?"
        message={`${confirm?.user.email ?? ''} will be signed out everywhere and get a new temporary password to share with them.`}
        confirmLabel="Reset password"
        onClose={() => setConfirm(null)}
        onConfirm={() => {
          const u = confirm!.user;
          void act(async () => {
            const r = await api.admin.resetPassword(u.id);
            setTempPassword({ email: u.email, password: r.tempPassword });
          });
        }}
      />
      <ConfirmDialog
        open={confirm?.kind === 'deactivate'}
        title="Deactivate user?"
        message={`${confirm?.user.email ?? ''} will be signed out and can't sign in until reactivated. Their decks are kept.`}
        confirmLabel="Deactivate"
        destructive
        onClose={() => setConfirm(null)}
        onConfirm={() => {
          const u = confirm!.user;
          void act(() => api.admin.updateUser(u.id, { active: false }));
        }}
      />
      <ConfirmDialog
        open={confirm?.kind === 'delete'}
        title="Delete user?"
        message={`${confirm?.user.email ?? ''} will be removed permanently. Their decks move to your account. This can't be undone.`}
        confirmLabel="Delete user"
        destructive
        onClose={() => setConfirm(null)}
        onConfirm={() => {
          const u = confirm!.user;
          void act(() => api.admin.deleteUser(u.id));
        }}
      />
    </Stack>
  );
}

function AiTab() {
  const setAiEnabled = useAuthStore((s) => s.setAiEnabled);
  const [settings, setSettings] = useState<AppSettings | null>(null);
  const [models, setModels] = useState<{ id: string; name: string }[]>([]);
  const [limit, setLimit] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    api.admin
      .getSettings()
      .then((r) => {
        setSettings(r.settings);
        setModels(r.models);
        setLimit(String(r.settings.aiDailyLimit));
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Could not load settings'));
  }, []);

  const save = async (patch: Partial<AppSettings>) => {
    setError(null);
    setSaved(false);
    try {
      const r = await api.admin.saveSettings(patch);
      setSettings(r.settings);
      setLimit(String(r.settings.aiDailyLimit));
      setAiEnabled(r.settings.aiEnabled);
      setSaved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save settings');
    }
  };

  if (!settings) {
    return error ? (
      <Alert severity="error">{error}</Alert>
    ) : (
      <Box sx={{ display: 'grid', placeItems: 'center', py: 6 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  const limitValue = Number(limit);
  const limitValid = limit !== '' && Number.isInteger(limitValue) && limitValue >= 0;

  return (
    <Stack spacing={3} sx={{ maxWidth: 480 }}>
      {error && <Alert severity="error">{error}</Alert>}
      {saved && (
        <Alert severity="success" onClose={() => setSaved(false)}>
          Settings saved.
        </Alert>
      )}

      <Box>
        <FormControlLabel
          control={<Switch checked={settings.aiEnabled} onChange={(e) => void save({ aiEnabled: e.target.checked })} />}
          label={<Typography variant="body2" sx={{ fontWeight: 600 }}>AI features</Typography>}
        />
        <Typography variant="caption" component="p">
          Draft with AI and Rewrite. When off, the buttons are hidden for everyone.
        </Typography>
      </Box>

      <TextField
        select
        label="Model"
        value={settings.aiModel}
        disabled={!settings.aiEnabled}
        onChange={(e) => void save({ aiModel: e.target.value })}
        helperText="Used for drafting decks and rewriting text. If it's busy, the app falls back to GPT-OSS 120B."
      >
        {models.map((m) => (
          <MenuItem key={m.id} value={m.id}>
            {m.name}
          </MenuItem>
        ))}
      </TextField>

      <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
        <TextField
          label="Daily limit per user"
          type="number"
          value={limit}
          disabled={!settings.aiEnabled}
          onChange={(e) => setLimit(e.target.value)}
          error={!limitValid}
          helperText={limitValid ? 'AI requests per person per day. 0 means no limit.' : 'Enter a whole number, 0 or more.'}
          slotProps={{ htmlInput: { min: 0, step: 1 } }}
          sx={{ flex: 1 }}
        />
        <Button
          variant="outlined"
          color="neutral"
          sx={{ mt: '2px' }}
          disabled={!settings.aiEnabled || !limitValid || limitValue === settings.aiDailyLimit}
          onClick={() => void save({ aiDailyLimit: limitValue })}
        >
          Save
        </Button>
      </Stack>
    </Stack>
  );
}

export function AdminDialog({ open, onClose }: Props) {
  const [tab, setTab] = useState<'users' | 'departments' | 'templates' | 'ai'>('users');
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" aria-labelledby="admin-title">
      <DialogTitle sx={{ pr: 7, pb: 0 }}>
        <Typography id="admin-title" variant="h6" component="h2">
          Admin settings
        </Typography>
        <IconButton aria-label="Close" onClick={onClose} sx={{ position: 'absolute', right: 16, top: 18 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
        <Tabs
          value={tab}
          onChange={(_, v: 'users' | 'departments' | 'templates' | 'ai') => setTab(v)}
          sx={{ mt: 1, minHeight: 40, '& .MuiTab-root': { minHeight: 40, textTransform: 'none', fontWeight: 600 } }}
        >
          <Tab value="users" label="Users" />
          <Tab value="departments" label="Departments" />
          <Tab value="templates" label="Templates" />
          <Tab value="ai" label="AI" />
        </Tabs>
      </DialogTitle>
      <DialogContent sx={{ minHeight: 460, borderTop: 1, borderColor: 'divider', '.MuiDialogTitle-root + &': { pt: 2.5 } }}>
        {open &&
          (tab === 'users' ? (
            <UsersTab />
          ) : tab === 'departments' ? (
            <DepartmentsTab />
          ) : tab === 'templates' ? (
            <TemplatesTab />
          ) : (
            <AiTab />
          ))}
      </DialogContent>
    </Dialog>
  );
}
